/**
 * Release Notes — lists published release_notes newest-first (Day 100). Version +
 * date + optional title + highlight bullets, all plain text (NO markdown renderer
 * — highlights are a plain string array, RBP:1379). RLS lets every authenticated
 * role read published rows.
 */
import { useState, useEffect, type JSX } from 'react';
import { Megaphone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { Database } from '../../../../src/lib/database.types';

type ReleaseNote = Database['public']['Tables']['release_notes']['Row'];

function toBullets(h: ReleaseNote['highlights']): string[] {
  return Array.isArray(h) ? h.filter((x): x is string => typeof x === 'string') : [];
}

function fmtDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function ReleaseNotes(): JSX.Element {
  useDocumentTitle('Release Notes');
  const [rows, setRows] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('release_notes')
        .select('*')
        // Published-only even for admins (the published docs site; drafts live in
        // the arbi-dev Release Notes Manager). Mirrors the WhatsNewWidget filter.
        .eq('is_published', true)
        .order('released_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (!active) return;
      setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-3xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Megaphone size={18} className="text-accent" />
          <h1 className="text-xl font-semibold text-ink-bright">Release Notes</h1>
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="arbi-card p-10 text-center">
            <p className="text-sm text-ink-secondary">No release notes published yet.</p>
            <p className="text-xs text-ink-muted mt-1">Updates will appear here as they ship.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const bullets = toBullets(r.highlights);
              return (
                <article key={r.id} className="arbi-card p-5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-accent">{r.version}</span>
                    {r.title && (
                      <span className="text-sm font-medium text-ink-primary">{r.title}</span>
                    )}
                    <span className="text-xs text-ink-muted">{fmtDate(r.released_on)}</span>
                  </div>
                  {bullets.length > 0 && (
                    <ul className="mt-2.5 space-y-1.5 list-disc ml-5">
                      {bullets.map((b, i) => (
                        <li key={i} className="text-sm text-ink-secondary">
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
